import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import StatusAlertModel from "../../../components/statusAlertModel/StatusAlertModel";
import Title from "../../../components/title/Title";
import { IBillDetails } from "../../../interfaces/billDetails";
import { ISuccessState } from "../../../interfaces/successState";
import { downloadBill, getBillDetails } from "../../../services/bill";
import styles from "./CurrentBill.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import AlertModal from "../../../components/alertModal/alertModal";
import { Box, Typography, Button, Divider } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import SelectOption, {
  ISelectOption,
} from "../../../components/selectOption/SelectOption";
import { BillDetails } from "../../../interfaces/billHistory";
import { getAccountNumbersByToken } from "../../../store/actions/fault.action";
import { useNavigate } from "react-router-dom";

interface IBillDetailsItem {
  title: string;
  amount: string;
  date: string;
  cardClass: string;
}

interface UserAccount {
  nickname: string;
  accNo: string;
}

const CurrentBill = () => {
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [billDetails, setBillDetails] = useState<IBillDetailsItem[]>([]);
  const [amount, setAmount] = useState("");
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const [showAlert, setShowAlert] = useState(false);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [dateOfBill, setDateOfBill] = useState<string>("");
  const [selectedVal, setSelectedVal] = useState<string>(""); // Initialize to empty string
  const navigate = useNavigate();

  const navigatePaymentSummary = () => {
    if (billDetails.length > 0 && selectedVal) {
      navigate("/billing/payBill", {
        state: {
          accountNo: selectedVal,
          amount: amount,
        },
      });
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<BillDetails>({});

  // Fetch account numbers on component mount
  useEffect(() => {
    accountNumberHandler();
  }, []);

  const accountNumberHandler = async () => {
    setIsLoading(true);
    try {
      const response = await getAccountNumbersByToken();
      if (response) {
        setAccounts(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const accountNumbers: ISelectOption[] = [
    { label: "Select Account Number", value: "" }, // Add placeholder option
    ...(accounts?.map((account: UserAccount) => ({
      label: account.nickname
        ? account?.accNo + " - " + account?.nickname
        : account?.accNo,
      value: account.accNo,
      data: account,
    })) || []),
  ];

  const message = `Please note that currently available latest e-bill is for the month of : ${dateOfBill} `;

  const setAccountNoHandler = (value: string) => {
    setSelectedVal(value);
  };

  const searchHandler = (value: string) => {
    if (!value) return;

    setIsLoading(true);
    getBillDetails(value)
      .then((billDetails: IBillDetails) => {
        const details: IBillDetailsItem[] = [
          {
            title: "Total Payable",
            amount: formatAmount(+billDetails.outstandingBalance),
            date: "Due on " + billDetails.paymentDueDate,
            cardClass: "card-warning",
          },
          {
            title: "Bill Amount",
            amount: formatAmount(+billDetails.billAmount),
            date: "For month ending at " + billDetails.lastBillDate,
            cardClass: "card-primary",
          },
          {
            title: "Last Payment Amount",
            amount: formatAmount(+billDetails.lastPaymentAmount),
            date: "Paid on " + billDetails.lastPaymentDate,
            cardClass: "card-success",
          },
        ];

        setBillDetails(details);
        setAmount(billDetails.outstandingBalance);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setBillDetails([]);
        setIsSuccess({
          isSuccess: false,
          message: error.errorMessage || "Failed to get bill details",
        });
      });
  };

  const formatAmount = (amount: number) => {
    const formattedAmount = new Intl.NumberFormat("en", {
      style: "currency",
      currency: "LKR",
    }).format(amount);

    const [mainAmount, cents] = formattedAmount.split(".");

    return `${mainAmount}.<span style="font-size: 0.8em;">${cents}</span>`;
  };

  const downloadHandler = () => {
    setIsLoading(true);
    if (!selectedVal) {
      return "N/A";
    }

    downloadBill(selectedVal)
      .then((resp) => {
        if (resp.dataBundle) {
          setDateOfBill(resp.successMessage);
          setShowAlertSuccess(true);
          const linkSource = `data:application/pdf;base64,${resp.dataBundle}`;
          const downloadLink = document.createElement("a");
          const fileName = "e-bill.pdf";
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setIsSuccess({
          isSuccess: false,
          message: error.response.data.errorMessage,
        });
      });
  };

  const detailsItem = (details: IBillDetailsItem[], styleClass: string) => {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box
          display="flex"
          borderRadius="20px"
          sx={{
            background: "linear-gradient(to right, #0D47A1, #1976D2)",
            color: "white",
            width: "100%",
            height: "250px",
            boxShadow: 5,
            p: 4,
            alignItems: "center",
          }}
        >
          {/* Total Payable Section */}
          <Box
            flex={1.2}
            p={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              backgroundColor: "#0D47A1",
              borderRadius: "12px",
            }}
          >
            <Typography variant="h6" fontWeight="500" gutterBottom>
              {details[0].title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              <span dangerouslySetInnerHTML={{ __html: details[0].amount }} />
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {details[0].title !== "Total Payable" ? details[0].date : ""}
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 3,
                backgroundColor: "#51B848",
                color: "white",
                borderRadius: "12px",
                padding: "10px 25px",
                fontSize: "16px",
                fontWeight: "bold",
                textTransform: "none",
                "&:hover": { backgroundColor: "#388E3C" },
              }}
              onClick={() => navigatePaymentSummary()}
            >
              Pay
            </Button>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              height: "80%",
            }}
          />

          {/* Last Bill Amount Section */}
          <Box
            flex={1}
            p={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="500" gutterBottom>
              {details[1].title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              <span dangerouslySetInnerHTML={{ __html: details[1].amount }} />
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {details[1].title !== "Total Payable" ? details[1].date : ""}
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.57)",
              height: "80%",
            }}
          />

          {/* Last Payment Amount Section  */}
          <Box
            flex={1}
            p={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="500" gutterBottom>
              {details[2].title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              <span dangerouslySetInnerHTML={{ __html: details[2].amount }} />
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {details[2].title !== "Total Payable" ? details[2].date : ""}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <div className="page-container">
      <AlertModal
        show={showAlert}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: () => {
              setShowAlert(false);
            },
            variant: "success",
          },
        ]}
      >
        <label>
          You are currently not registered for SLT E-Bill service. Please
          contact your Account Manager
        </label>
      </AlertModal>

      <AlertModal
        show={showAlertSuccess}
        variant="success"
        buttons={[
          {
            text: "Ok",
            onClick: () => {
              setShowAlertSuccess(false);
            },
            variant: "success",
          },
        ]}
      >
        <label>{message}</label>
      </AlertModal>
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/billing/BillSummary", title: "Bill Summary" },
        ]}
      />
      <Title>Bill Summary</Title>

      {billDetails.length > 0 ? (
        <Row className="page-sub-containerV3 ">
          <Col>
            <Controller
              control={control}
              name="accountNumber"
              rules={{ required: true }}
              render={({ field }) => (
                <SelectOption
                  label="Account Number"
                  onChange={(val: ISelectOption) => {
                    field.onChange(val.value);
                    searchHandler(val.value);
                    setAccountNoHandler(val.value);
                  }}
                  required
                  options={accountNumbers}
                  placeholder="Select Account Number"
                  value={accountNumbers.find(
                    (option) => option.value === field.value
                  )}
                  errors={[
                    {
                      isShow: errors.accountNumber?.type === "required",
                      message: "Account number is required",
                    },
                  ]}
                />
              )}
            />
          </Col>
          <Row className="mt-4">
            <Col className={styles.details}>
              <>{detailsItem(billDetails, styles.totalPayable)}</>
            </Col>
          </Row>
          <Row className="justify-content-end mt-3">
            <Col className="col-auto">
              <div className={styles.btnAddUser} onClick={downloadHandler}>
                <span>Download E-bill</span>
                <div className={styles.icon}>
                  <FontAwesomeIcon icon={faDownload} />
                </div>
              </div>
            </Col>
          </Row>
        </Row>
      ) : (
        <>
          <Col className="mt-1">
            <Controller
              control={control}
              name="accountNumber"
              rules={{ required: true }}
              render={({ field }) => (
                <SelectOption
                  label="Account Number"
                  onChange={(val: ISelectOption) => {
                    field.onChange(val.value);
                    searchHandler(val.value);
                    setAccountNoHandler(val.value);
                  }}
                  required
                  options={accountNumbers}
                  value={accountNumbers.find(
                    (option) => option.value === field.value
                  )}
                  errors={[
                    {
                      isShow: errors.accountNumber?.type === "required",
                      message: "Account number is required",
                    },
                  ]}
                />
              )}
            />
          </Col>
          <EmptyPlaceholder className="mt-5" />
        </>
      )}

      {/* Add status message */}
      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default CurrentBill;
